import { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle, Download, RefreshCw, Search, X, ChevronDown, ChevronRight, Loader2, ShieldAlert, Wifi, WifiOff, Lock, Bug, Zap, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { EVENT_SEVERITIES, EVENT_CATEGORIES } from '../lib/eventLog';
import { DataTable, type ColumnDef } from './DataTable';

interface EventLogEntry {
  id: string;
  severity: string;
  category: string;
  message: string;
  metadata: Record<string, unknown> | null;
  user_id: string | null;
  user_email: string;
  created_at: string;
}

interface ListEnvelope {
  rows: EventLogEntry[];
  total: number;
}

interface SummaryStats {
  errors_24h: number;
  warnings_24h: number;
  rls_rejections_24h: number;
}

const SEVERITY_LABELS: Record<string, string> = {
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
  debug: 'Debug',
};

const SEVERITY_COLORS: Record<string, string> = {
  error: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
  debug: 'bg-subtle text-theme-secondary',
};

const SEVERITY_DOT_COLORS: Record<string, string> = {
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
  debug: 'bg-gray-400',
};

const CATEGORY_LABELS: Record<string, string> = {
  rls_rejection: 'RLS Rejection',
  api_error: 'API Error',
  network_error: 'Network Error',
  auth_error: 'Auth Error',
  validation_error: 'Validation Error',
  edge_function_error: 'Edge Function Error',
  unexpected_state: 'Unexpected State',
};

const CATEGORY_ICONS: Record<string, typeof AlertTriangle> = {
  rls_rejection: ShieldAlert,
  api_error: Bug,
  network_error: WifiOff,
  auth_error: Lock,
  validation_error: AlertTriangle,
  edge_function_error: Zap,
  unexpected_state: HelpCircle,
};

const CATEGORY_COLORS: Record<string, string> = {
  rls_rejection: 'text-red-600',
  api_error: 'text-orange-600',
  network_error: 'text-slate-600',
  auth_error: 'text-rose-600',
  validation_error: 'text-amber-600',
  edge_function_error: 'text-sky-600',
  unexpected_state: 'text-theme-secondary',
};

const DATE_PRESETS = [
  { label: 'Last Hour', hours: 1 },
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 3 Days', hours: 72 },
  { label: 'Last 7 Days', hours: 168 },
];

function getDateRange(hours: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function EventLogViewer() {
  const { t } = useTranslation('common');
  const [logs, setLogs] = useState<EventLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [datePreset, setDatePreset] = useState(24);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [stats, setStats] = useState<SummaryStats>({ errors_24h: 0, warnings_24h: 0, rls_rejections_24h: 0 });
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [datePreset, filterSeverity, filterCategory, filterUser, debouncedSearch, pageSize]);

  const buildParams = useCallback((override: Record<string, string> = {}) => {
    const { from, to } = getDateRange(datePreset);
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    if (filterSeverity) params.set('severity', filterSeverity);
    if (filterCategory) params.set('category', filterCategory);
    if (filterUser) params.set('user_email', filterUser);
    if (debouncedSearch) params.set('search', debouncedSearch);
    params.set('sort_by', sortKey);
    params.set('sort_dir', sortDir);
    params.set('limit', String(pageSize));
    params.set('offset', String(page * pageSize));
    for (const [k, v] of Object.entries(override)) params.set(k, v);
    return params;
  }, [datePreset, filterSeverity, filterCategory, filterUser, debouncedSearch, sortKey, sortDir, pageSize, page]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.get<ListEnvelope>(`/api/event-logs?${buildParams().toString()}`);
      setLogs(result.rows);
      setTotalCount(result.total);
    } catch (err) {
      console.error('Error fetching event logs:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiClient.get<SummaryStats>('/api/event-logs/stats');
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await apiClient.get<string[]>('/api/event-logs/users');
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    void fetchStats();
    void fetchUsers();
  }, [fetchStats, fetchUsers]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams({ limit: '10000', offset: '0' });
      const result = await apiClient.get<ListEnvelope>(`/api/event-logs?${params.toString()}`);
      const rows = result.rows.map((row) => ({
        Timestamp: new Date(row.created_at).toISOString(),
        Severity: SEVERITY_LABELS[row.severity] || row.severity,
        Category: CATEGORY_LABELS[row.category] || row.category,
        Message: row.message,
        User: row.user_email || '',
        Metadata: row.metadata ? JSON.stringify(row.metadata) : '',
      }));

      const headers = Object.keys(rows[0] || {});
      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          headers.map((h) => {
            const val = String((row as Record<string, string>)[h] || '');
            return val.includes(',') || val.includes('"') || val.includes('\n')
              ? `"${val.replace(/"/g, '""')}"`
              : val;
          }).join(','),
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setDatePreset(24);
    setFilterSeverity('');
    setFilterCategory('');
    setFilterUser('');
    setSearchQuery('');
    setDebouncedSearch('');
  };

  const hasActiveFilters = filterSeverity || filterCategory || filterUser || searchQuery;

  const columns: ColumnDef<EventLogEntry>[] = useMemo(() => [
    {
      key: 'created_at',
      label: t('eventLog.time', 'Time'),
      sortable: true,
      headerClassName: 'w-40',
      render: (entry) => (
        <span className="text-xs text-theme-secondary font-mono whitespace-nowrap">
          {formatTimestamp(entry.created_at)}
        </span>
      ),
    },
    {
      key: 'severity',
      label: t('eventLog.severity', 'Severity'),
      sortable: true,
      headerClassName: 'w-24',
      render: (entry) => (
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_DOT_COLORS[entry.severity] || 'bg-gray-400'}`} />
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[entry.severity] || 'bg-subtle text-theme-secondary'}`}>
            {SEVERITY_LABELS[entry.severity] || entry.severity}
          </span>
        </div>
      ),
    },
    {
      key: 'category',
      label: t('eventLog.category', 'Category'),
      sortable: true,
      headerClassName: 'w-36',
      render: (entry) => {
        const Icon = CATEGORY_ICONS[entry.category] || HelpCircle;
        const color = CATEGORY_COLORS[entry.category] || 'text-theme-secondary';
        return (
          <div className="flex items-center gap-1.5">
            <Icon size={14} className={color} />
            <span className="text-xs text-theme-secondary font-medium">
              {CATEGORY_LABELS[entry.category] || entry.category}
            </span>
          </div>
        );
      },
    },
    {
      key: 'message',
      label: t('eventLog.message', 'Message'),
      render: (entry) => (
        <p className="text-sm text-theme-primary truncate max-w-md" title={entry.message}>
          {entry.message}
        </p>
      ),
    },
    {
      key: 'user_email',
      label: t('eventLog.user', 'User'),
      sortable: true,
      headerClassName: 'w-40 hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: (entry) => (
        <span className="text-xs text-theme-secondary truncate block max-w-36">{entry.user_email || 'System'}</span>
      ),
    },
    {
      key: 'expand',
      label: '',
      headerClassName: 'w-8',
      render: (entry) => entry.metadata ? (
        <button
          onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === entry.id ? null : entry.id); }}
          className="p-1 rounded hover:bg-subtle transition-colors text-theme-secondary"
        >
          {expandedId === entry.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : null,
    },
  ], [expandedId, t]);

  const buildRowTooltip = useCallback((entry: EventLogEntry): string => {
    if (!entry.metadata || expandedId === entry.id) return '';
    const lines: string[] = [];
    lines.push(`[${SEVERITY_LABELS[entry.severity]}] ${CATEGORY_LABELS[entry.category] || entry.category}`);
    lines.push(entry.message);
    if (entry.user_email) lines.push(`User: ${entry.user_email}`);
    lines.push(`Time: ${formatTimestamp(entry.created_at)}`);
    lines.push('');
    lines.push('--- Metadata ---');
    for (const [key, value] of Object.entries(entry.metadata)) {
      lines.push(`${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}`);
    }
    return lines.join('\n');
  }, [expandedId]);

  const renderExpandedRow = (entry: EventLogEntry) => {
    if (!entry.metadata) return null;
    return (
      <div className="bg-subtle rounded-lg border border-line p-4 space-y-2">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="text-xs font-semibold text-theme-secondary uppercase tracking-wider">{t('eventLog.metadata', 'Metadata')}</h4>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[entry.severity]}`}>
            {SEVERITY_LABELS[entry.severity]}
          </span>
        </div>
        <p className="text-sm text-theme-primary mb-3 bg-surface p-2 rounded border border-line">{entry.message}</p>
        <div className="space-y-1">
          {Object.entries(entry.metadata).map(([key, value]) => (
            <div key={key} className="flex items-start gap-2 text-sm">
              <span className="font-medium text-theme-secondary min-w-28 flex-shrink-0">{key}:</span>
              <span className="text-theme-secondary break-all font-mono text-xs">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '')}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const rowClassName = (entry: EventLogEntry): string => {
    if (entry.severity === 'error') return 'bg-red-50/50 hover:bg-red-50 dark:bg-red-500/10 dark:hover:bg-red-500/10';
    if (entry.severity === 'warning') return 'bg-amber-50/30 hover:bg-amber-50 dark:bg-amber-500/10 dark:hover:bg-amber-500/10';
    return '';
  };

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            <h1 className="text-2xl font-bold text-theme-primary">{t('eventLog.title', 'Event Log')}</h1>
          </div>
          <p className="text-sm text-theme-secondary mt-1">{t('eventLog.subtitle', 'Track errors, warnings, RLS rejections, and system events')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { void fetchLogs(); void fetchStats(); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-subtle text-theme-secondary hover:bg-subtle rounded-lg transition-colors text-sm"
          >
            <RefreshCw size={14} />
            {t('integrations.refresh', 'Refresh')}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors text-sm disabled:opacity-50"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {t('eventLog.exportCsv', 'Export CSV')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4 flex-shrink-0">
        <div className="bg-surface rounded-lg border border-red-200 p-4 dark:border-red-500/30">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <p className="text-xs font-medium text-theme-secondary uppercase tracking-wider">{t('eventLog.errors', 'Errors')} (24h)</p>
          </div>
          <p className="text-2xl font-bold text-red-700 mt-1 dark:text-red-300">{stats.errors_24h.toLocaleString()}</p>
        </div>
        <div className="bg-surface rounded-lg border border-amber-200 p-4 dark:border-amber-500/30">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
            <p className="text-xs font-medium text-theme-secondary uppercase tracking-wider">{t('eventLog.warnings', 'Warnings')} (24h)</p>
          </div>
          <p className="text-2xl font-bold text-amber-700 mt-1 dark:text-amber-300">{stats.warnings_24h.toLocaleString()}</p>
        </div>
        <div className="bg-surface rounded-lg border border-rose-200 p-4 dark:border-rose-500/30">
          <div className="flex items-center gap-2">
            <ShieldAlert size={14} className="text-rose-500" />
            <p className="text-xs font-medium text-theme-secondary uppercase tracking-wider">{t('eventLog.rlsRejections', 'RLS Rejections')} (24h)</p>
          </div>
          <p className="text-2xl font-bold text-rose-700 mt-1 dark:text-rose-300">{stats.rls_rejections_24h.toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-line p-3 mb-4 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 bg-subtle rounded-lg p-1">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.hours}
                onClick={() => setDatePreset(preset.hours)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  datePreset === preset.hours
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-theme-secondary hover:bg-subtle'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="text-sm border border-line rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface text-theme-primary"
          >
            <option value="">{t('eventLog.allSeverities', 'All Severities')}</option>
            {Object.values(EVENT_SEVERITIES).map((val) => (
              <option key={val} value={val}>{SEVERITY_LABELS[val] || val}</option>
            ))}
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="text-sm border border-line rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface text-theme-primary"
          >
            <option value="">{t('eventLog.allCategories', 'All Categories')}</option>
            {Object.values(EVENT_CATEGORIES).map((val) => (
              <option key={val} value={val}>{CATEGORY_LABELS[val] || val}</option>
            ))}
          </select>

          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="text-sm border border-line rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface text-theme-primary"
          >
            <option value="">{t('eventLog.allUsers', 'All Users')}</option>
            {users.map((email) => (
              <option key={email} value={email}>{email}</option>
            ))}
          </select>

          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="text"
              placeholder={t('eventLog.searchPlaceholder', 'Search messages or emails...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-8 py-1.5 text-sm border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setDebouncedSearch(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-theme-secondary hover:text-theme-secondary"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
            >
              {t('eventLog.clearFilters', 'Clear filters')}
            </button>
          )}
        </div>
      </div>

      <DataTable
        data={logs}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyIcon={<Wifi size={40} className="text-theme-secondary" />}
        emptyTitle={t('eventLog.noEvents', 'No events found')}
        emptyDescription={t('eventLog.noEventsHint', 'Try adjusting your date range or filters')}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        serverPagination
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onRowClick={(entry) => {
          if (entry.metadata) {
            setExpandedId(expandedId === entry.id ? null : entry.id);
          }
        }}
        expandedId={expandedId}
        expandedRow={renderExpandedRow}
        rowTitle={buildRowTooltip}
        rowClassName={rowClassName}
      />
    </div>
  );
}
